import { useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { PatientHistoryType } from 'src/graphql.generated'
import { DEFAULT_INTAKE_NOTE_TTL_IN_HOURS } from 'src/participantHistory/constants'
import { clearLocal, readLocal, writeLocal } from './localStore'

type Values = Record<string, unknown>

const SAVE_DELAY_MS = 300

// Standard's slot (`persistFormHelpers`): its key, and the longer life it gives an intake note.
const slotOf = (uid: string, type: PatientHistoryType) => `PatientHistoryForm-${uid}-${type}`
const INTAKE_NOTE_SLOT_TYPES: PatientHistoryType[] = [
  PatientHistoryType.IntakeNote,
  PatientHistoryType.IntakeNotePsychiatry,
]
const DRAFT_EXPIRY_HOURS = 24

const expiryOf = (type: PatientHistoryType) =>
  INTAKE_NOTE_SLOT_TYPES.includes(type) ? DEFAULT_INTAKE_NOTE_TTL_IN_HOURS : DRAFT_EXPIRY_HOURS

const readHistoryDraft = (uid: string, type: PatientHistoryType): Values | null => {
  const stored = readLocal<unknown>(slotOf(uid, type), null, { expiresInHours: expiryOf(type) })

  return typeof stored === 'object' && stored !== null && !Array.isArray(stored) ? (stored as Values) : null
}

export const clearHistoryDraft = (uid: string, type: PatientHistoryType): void => clearLocal(slotOf(uid, type))

// Read from Standard's slot, so a note drafted on one surface is restored on the other.
export const useRestoredDraft = (uid: string, type: PatientHistoryType): Values | null =>
  useState(() => readHistoryDraft(uid, type))[0]

// True once the form differs from how it opened; only then is the draft written.
export const useDraftSaver = (uid: string, type: PatientHistoryType, value: Values): boolean => {
  const serialized = JSON.stringify(value)
  const opened = useRef(serialized)
  const dirty = serialized !== opened.current
  const latest = useRef(value)
  latest.current = value
  const save = useMemo(
    () =>
      debounce(() => {
        writeLocal(slotOf(uid, type), latest.current, { expiresInHours: expiryOf(type) })
      }, SAVE_DELAY_MS),
    [uid, type]
  )

  useEffect(() => {
    if (dirty) {
      save()
    }
  }, [dirty, serialized, save])

  useEffect(() => () => save.cancel(), [save])

  return dirty
}
